import get from 'lodash/get'
import set from 'lodash/set'
import remove from 'lodash/remove'
import isObject from 'lodash/isObject'
import router from '@/router'
import { arrayIt, hasProp, siteId, siteIds, ruleHelpers, appData, dates, storage, requests } from '@/utils'

let fetchCompleted = 0
const needsFetch = () => {
	const timePassed = new Date().getTime() - fetchCompleted
	const HOUR = 60 * 60 * 1000

	return timePassed > HOUR
}
export const perPage = 20

const setFromState = (payload, state) => {
	if (!payload.campaignIds || !payload.campaignIds.length) {
		if ('all' !== state.embedFilter) {
			payload.campaignIds = state.filters.campaignIds
		}
	}

	if (!payload.offset) {
		payload.offset = perPage * (state.page - 1)
	}

	if (!payload.searchTerm) {
		payload.searchTerm = state.search
	}

	if (!payload.starts) {
		payload.starts = state.filters.starts
		payload.ends = state.filters.ends
	}

	if (!payload.campaignType) {
		payload.campaignType = state.filters.campaignType
	}

	if (!payload.status) {
		payload.status = state.filters.status
	}

	if (!payload.sort) {
		payload.sort = state.sort
	}

	payload.limit = payload.limit || perPage

	return payload
}

const addError = (commit, error, explanation) => {
	error.explanation = explanation
	if (!hasProp(error, 'closes')) {
		error.closes = true
	}
	commit('maybeAddError', error, { root: true })
}

export default {
	fetchDashboard ({ commit, state, dispatch, rootGetters }, payload = {}) {
		const loadingKey = needsFetch() ? 'campaigns' : payload.loading || 'campaigns'
		return new Promise((resolve, reject) => {
			if (!needsFetch() && !payload.force) {
				return resolve()
			}

			if (!rootGetters.connected) {
				return resolve()
			}

			const loader = (done = false) => {
				commit(done ? 'doneLoading' : 'loading', loadingKey, { root: true })
			}

			const post = {}
			let params = []
			let query = ''
			let mainQuery = true
			let promise1 = Promise.resolve()

			loader()

			payload = setFromState(payload, state)

			const postRequest = payload.campaignIds && payload.campaignIds.length

			const addParam = (key, val) => {
				if (postRequest) {
					post[ key ] = val
				} else {
					params.push(`${key}=${val}`)
				}
			}

			addParam('site', siteIds ? siteIds.join(',') : siteId)

			if (payload.offset) {
				addParam('offset', payload.offset)
			}

			if (payload.limit) {
				addParam('limit', payload.limit)
			}

			if (payload.sort) {
				addParam('sort', payload.sort)
			}

			if (payload.starts && payload.ends) {
				addParam('starts', payload.starts)
				addParam('ends', payload.ends)
				mainQuery = false
			}

			if (payload.campaignType) {
				addParam('campaignType', payload.campaignType)
				mainQuery = false
			}

			if (payload.status) {
				addParam('status', payload.status)
				mainQuery = false
			}

			// If we are doing a search, reset all params, except offset.
			if (payload.searchTerm) {
				addParam('search', encodeURIComponent(payload.searchTerm))
				if (payload.offset) {
					addParam('offset', payload.offset)
				}
				mainQuery = false
			}

			if (!postRequest) {
				query = params.length ? `?${params.join('&')}` : ''
			} else {
				addParam('campaignIds', payload.campaignIds.join(','))
				mainQuery = false
			}

			if (rootGetters.shouldFetchUser) {
				promise1 = dispatch('fetchMe', {}, { root: true })
					.catch(error => {
						console.error({ error })
						return reject(error)
					})
			}

			commit('closeAlerts', null, { root: true })

			const promise2 = new Promise((resolve, reject) => {
				this._vm.$api[postRequest ? 'post' : 'get']({
					endpoint : `campaign-dashboard${query}`,
					cached   : false,
				})
					.send(postRequest ? post : null)
					.then(({ body }) => {
						// eslint-disable-next-line camelcase
						const { campaigns, total_campaigns_count: total, folders } = body
						if (mainQuery) {
							commit('fetched', 'mainQuery', { root: true })
							commit('setTotalCampaignCount', total)
						}

						// Initiate rulesets.
						campaigns.forEach(campaign => { campaign.rulesets = [] })
						const campaignIds = campaigns.map(campaign => campaign.id)

						if (!payload.offset || payload.reset) {
							commit('setCampaigns', campaigns)
						} else {
							commit('addCampaigns', campaigns)
						}
						commit('updateSort')

						dispatch('setFolders', folders)
						commit('setTotalFilteredCampaignCount', total)

						if (total) {
							dispatch('refreshAnalytics')
						}

						if (payload.searchTerm) {
							state.campaigns.some(campaign => {
								if (campaign.status.includes('trash', 'archive')) {
									commit('maybeAddAlert', {
										message : 'Search results contain items that have been archived or placed in the trash.',
										type    : 'warn',
									}, { root: true })

									return true
								}
							})
						}

						dispatch('fetchCampaignsRulesets', campaignIds)

						loader(true)

						resolve()
					})
					.catch(error => {
						addError(commit, error, 'Something went wrong while fetching campaign data.')
						loader(true)
						reject(error)
						console.error({ error })
					})
					.finally(() => {
						fetchCompleted = new Date().getTime()
					})
			})

			const handleError = (error) => {
				// Only output 'no-campaigns-error' error if user has campaigns from dashboard request.
				// See: https://github.com/awesomemotive/optin-monster-support/issues/384
				if ('no-campaigns-error' !== get(error, 'response.body.code') || state.totalCampaignsCount) {
					addError(commit, error, error.explanation || 'Something went wrong while fetching WordPress data.')
				}
				reject(error)
				console.error({ error })
			}

			Promise.all([ promise1, promise2 ])
				.then(() => {
					dispatch('wp/fetchWpResources', { exclude: [ 'posts', 'post_types', 'taxonomies' ] }, { root: true })
						.then((args) => {
							if (args.siteIds && 1 < args.siteIds.length) {
								// If the site ids were auto-updated through the fetch reqeust,
								// we need to reload the page now, to trigger a fresh refresh.
								if (!siteIds || args.siteIds.join(',') !== siteIds.join(',') || siteId !== args.siteId) {
									window.location.reload()
								}
							}

							Promise.all([
								dispatch('fetchDraftCampaignCount'),
								dispatch('fetchPublishCampaignCount'),
							]).catch(handleError)
						}).catch(handleError)
				})
				.then(() => {
					resolve()
				})
				.catch(handleError)
		})
	},
	refreshDashboard ({ dispatch }) {
		return dispatch('fetchDashboard', {
			force   : true,
			reset   : true,
			loading : 'campaigns-refresh',
		})
	},
	resetFilters ({ state, commit, dispatch }, { keys = null, handler = false } = {}) {
		const { search, page } = state

		if (null === keys) {
			commit('resetFilters')
			this._vm.$bus.$emit('resetAllCampaignFilters')
		} else {
			arrayIt(keys).forEach(k => {
				commit('resetFilter', k)
			})
		}

		const afterReset = () => {
			if (search || 1 < page) {
				dispatch('goToPage', { search: '', page: 1 })
			} else {
				dispatch('refreshDashboard')
			}
		}

		if (handler) {
			return handler(afterReset)
		}

		afterReset()
	},
	fetchCampaigns ({ dispatch }, {
		campaignIds,
		...rest
	}) {
		// Ensure array of unique Ids.
		campaignIds = [ ...new Set(campaignIds) ]

		const promises = []
		if (0 < campaignIds.length) {
			campaignIds.forEach(slug => {
				promises.push(dispatch('fetchCampaign', {
					campaignId : slug,
					...rest,
				}))
			})
		}

		return Promise.all(promises)
	},
	fetchCampaign ({ commit, getters, dispatch, rootGetters }, {
		campaignId,
		loading = 'campaigns',
	}) {
		if (!rootGetters.connected) {
			return
		}

		const loader = (done = false) => {
			if (loading) {
				commit(done ? 'doneLoading' : 'loading', 'campaigns', { root: true })
			}
		}
		loader()

		return new Promise((resolve, reject) => {
			const complete = () => {
				loader(true)
				return resolve()
			}

			const campaign = getters.getCampaign(campaignId)

			if (campaign && campaign.id) {
				return complete()
			}

			this._vm.$api
				.get(`campaign-dashboard?campaign=${campaignId}&site=${siteIds ? siteIds.join(',') : siteId}`)
				.then(({ body }) => {
					const { campaigns } = body

					dispatch('fetchFolders')

					// Initiate rulesets.
					campaigns.forEach(campaign => { campaign.rulesets = [] })
					const campaignIds = campaigns.map(campaign => campaign.id)

					commit('addCampaigns', campaigns)
					dispatch('fetchCampaignsRulesets', campaignIds)

					complete()
				})
				.catch(error => {
					// Campaign-specific error.
					commit('setError', true)

					let explanation = 'Something went wrong while fetching this campaign\'s data.'
					if (404 === error.status) {
						explanation += ' Has this campaign been deleted?<br>'
					}
					addError(commit, error, explanation)
					loader(true)
					reject(error)
					console.error({ error })
				})
				.finally(() => {
					// We don't want the dashboard to be prevented from fetching.
					fetchCompleted = 0
					loader(true)
				})
		})
	},
	fetchCampaignsRulesets ({ commit, dispatch }, campaigns = []) {
		return new Promise((resolve, reject) => {
			const promises = []
			const update = (campaignId, value, valueType = 'array') => {
				const key = 'rulesets'
				commit('updateCampaignValue', { campaignId, key, value, valueType })
			}
			campaigns.forEach(campaign => {
				const campaignId = get(campaign, 'id', campaign)

				promises.push(
					dispatch('fetchRulesets', { campaign })
						.then(rulesets => {
							update(campaignId, rulesets)
						})
						.catch(error => {
							update(campaignId, error, 'object')
						})
				)
			})

			Promise.all(promises)
				.then(() => {
					resolve(campaigns)
				})
				.catch(error => {
					console.error({ error })
					reject(error)
				})
		})
	},
	fetchRulesets ({ commit, getters, rootGetters }, { campaign, fresh = false }) {
		return new Promise((resolve, reject) => {
			if (!rootGetters.connected) {
				return resolve([])
			}

			campaign = isObject(campaign) ? campaign : getters.getCampaign(campaign)

			const campaignId = get(campaign, 'id')
			if (!campaignId) {
				return reject(new Error(`Cannot find campaign for this id: ${campaignId}`))
			}

			if (campaign.is_legacy) {
				return resolve([])
			}

			if (!fresh && campaign.rulesets && 0 < campaign.rulesets.length) {
				return resolve(campaign.rulesets)
			}

			this._vm.$api
				.get(`campaigns/${campaignId}/rulesets`)
				.then(response => {
					resolve(response.body)
				})
				.catch(error => {
					addError(commit, error, `Something went wrong while fetching ruleset data for this campaign: ${campaign.id}`)

					reject(error)
					console.error({ error, campaignId })
				})
		})
	},
	refreshAnalytics ({ commit, state }) {
		return new Promise((resolve, reject) => {
			const campaignIds = []
			state.campaigns.forEach(c => {
				campaignIds.push(c.id)

				if (c.splits.length) {
					c.splits.forEach(s => campaignIds.push(s.id))
				}
			})
			this._vm.$api
				.post('campaign-dashboard/analytics')
				.send({
					campaigns : campaignIds,
				})
				.then(response => {
					commit('refreshAnalytics', response.body)
					resolve()
				})
				.catch(error => {
					reject(error)
					console.error({ error })
				})
		})
	},
	fetchDraftCampaignCount ({ commit, dispatch, getters }) {
		return new Promise((resolve, reject) => {
			return dispatch('fetchStatusCampaignCount', {
				campaignIds : getters.disabledCampaignIds.join(','),
				status      : 'pause',
			})
				.then((total) => {
					commit('draftCountUpdate', total)
					resolve(total)
				})
				.catch(error => {
					reject(error)
					console.error({ error })
				})
		})
	},
	fetchPublishCampaignCount ({ commit, dispatch, getters }) {
		return new Promise((resolve, reject) => {
			return dispatch('fetchStatusCampaignCount', {
				campaignIds : getters.enabledCampaignIds.join(','),
				status      : 'active',
			})
				.then((total) => {
					commit('publishCountUpdate', total)
					resolve(total)
				})
				.catch(error => {
					reject(error)
					console.error({ error })
				})
		})
	},
	fetchStatusCampaignCount ({ commit, state }, params = {}) {
		return new Promise((resolve, reject) => {
			params = {
				...params,
				site  : siteIds ? siteIds.join(',') : siteId,
				limit : 1,
			}

			params = Object.keys(params).map(p => {
				return `${p}=${params[p]}`
			}).join('&')

			this._vm.$api.get({
				endpoint : `campaign-dashboard?${params}`,
				cached   : false,
			})
				.then(({ body }) => {
					resolve(body.total_campaigns_count)
				})
		})
	},
	fetchWpDataForCampaign ({ commit, dispatch }, { campaignId }) {
		return new Promise((resolve, reject) => {
			const handleError = (error) => {
				addError(commit, error, error.explanation || 'Something went wrong while fetching WordPress settings.')
				reject(error)
				console.error({ error })
			}
			return this._vm.$api
				.get({ wpEndpoint: `omapp/v1/campaigns/${campaignId}`, cached: false })
				.then(response => {
					Promise.all([
						dispatch('updateCampaignsWithWpData', [ response.body ]),
						dispatch('wp/fetchWpResources', {}, { root: true }),
					]).catch(handleError)

					resolve()
				})
				.catch(handleError)
		})
	},
	updateCampaignsWithWpData ({ commit, state, dispatch }, wpCampaigns) {
		wpCampaigns.forEach(wpData => {
			const campaign = state.campaigns.find((c) => c.id === wpData.id)
			if (campaign) {
				campaign.wp = wpData
				commit('updateCampaign', { campaign })
			}
		})
	},
	splitCampaign ({ dispatch }, { campaignId, params }) {
		return dispatch('duplicateCampaign', {
			campaignId,
			params,
			split : true,
		})
	},
	duplicateCampaign ({ dispatch, commit }, { campaignId, params, split = false }) {
		return new Promise((resolve, reject) => {
			this._vm.$api
				.post(`campaigns/${campaignId}/${split ? 'split' : 'duplicate'}`)
				.send(params)
				.then(({ body }) => {
					dispatch('refreshDashboard').then(() => resolve(body))
				})
				.catch(error => {
					addError(commit, error, `${split ? 'Split test' : 'Duplicate'} could not be created.`)
					reject(error)
					console.error({ error, campaignId })
				})
		})
	},
	createCampaign ({ commit }, campaignData) {
		return new Promise((resolve, reject) => {
			this._vm.$api
				.post('campaigns')
				.send(campaignData)
				.then(({ body }) => {
					resolve(body)
				})
				.catch(({ ...error }) => {
					let explanation = 'Something went wrong, and campaign creation failed.'

					if (error.status && '401' === String(error.status)) {
						const url = this._vm.$urls.newCampaign({
							siteIds      : get(campaignData, 'sites', []),
							template     : get(campaignData, 'template', ''),
							campaignName : get(campaignData, 'name', ''),
							campaignType : get(campaignData, 'type', ''),
						})
						window.open(url, '_blank')

						explanation += ' We have redirected you to the new-campaign page to finish creating this campaign.'
					}

					commit('closeAlerts', null, { root: true })
					addError(commit, error, explanation)

					reject(error)
					console.error({ error })
				})
		})
	},
	setStatus ({ commit, getters }, payload) {
		let { status, campaigns } = payload

		const prevStatuses = {}
		const prevSplitStatuses = {}
		campaigns.slice(0).forEach(campaignId => {
			const campaign = getters.getCampaign(campaignId)
			if (campaign.status === status) {
				return remove(payload.campaigns, (c) => c === campaignId)
			}

			prevStatuses[campaignId] = campaign.status

			// If we are pausing a parent and have split tests, lets pause the splits too.
			if ('paused' === status) {
				const splits = get(campaign, 'splits', [])
				if (splits && splits.length) {
					splits.forEach(split => {
						payload.campaigns.push(split.id)
						set(prevSplitStatuses, `${campaignId}.${split.id}`, split.status)
					})
				}
			}
		})

		return new Promise((resolve, reject) => {
			if (!payload.campaigns.length) {
				return resolve()
			}

			commit('setStatus', payload)

			this._vm.$api
				.post(`campaigns/batch/${'active' === status ? 'resume' : 'pause'}`)
				.send({
					campaigns : payload.campaigns,
				})
				.then(() => resolve())
				.catch(error => {
					if (403 === error.status) {
						// Let's attept to reset the statuses that could not be changed.
						const pattern = new RegExp(/campaign ([a-z|A-Z|0-9]+)\./g)
						const matches = pattern.exec(get(error, 'response.body.error', ''))
						const campaignId = matches[1] && matches[1].length ? matches[1] : ''
						if (campaignId) {
							if (prevStatuses[campaignId]) {
								commit('setCampaignStatus', {
									campaignId,
									status : prevStatuses[campaignId],
								})
							}
							if (prevSplitStatuses[campaignId]) {
								Object.keys(prevSplitStatuses[campaignId]).forEach(splitId => {
									commit('setCampaignStatus', {
										campaignId,
										splitId,
										status : prevStatuses[campaignId][splitId],
									})
								})
							}
						}
					}

					addError(commit, error, 'Campaign status could not be updated.')

					reject(error)
					console.error({ error })
				})
		})
	},
	trashCampaign ({ dispatch }, campaign) {
		return dispatch('deleteCampaign', {
			id      : campaign.id,
			toTrash : true,
		})
	},
	deleteCampaign ({ state, commit, dispatch }, { id, toTrash = false }) {
		const campaignIndex = state.campaigns.findIndex(c => c.id === id)
		return new Promise((resolve, reject) => {
			let route = `campaigns/${id}`
			if (toTrash) {
				route += '/trash'
			}
			this._vm.$api[toTrash ? 'post' : 'delete'](route)
				.then(() => {
					commit('removeCampaignByIndex', campaignIndex)
					dispatch('fillCampaignList', 1)
					resolve()
				})
				.catch(error => {
					addError(commit, error, `Campaign could not be ${toTrash ? 'trashed' : 'deleted'}.`)
					reject(error)
					console.error({ error, campaignId: id })
				})
		})
	},
	// eslint-disable-next-line camelcase
	deleteSplitCampaign ({ state, commit, dispatch }, { id, parent_id }) {
		// eslint-disable-next-line camelcase
		const campaignIndex = state.campaigns.findIndex(c => c.id === parent_id)
		const splitIndex = state.campaigns[campaignIndex].splits.findIndex(c => c.id === id)

		return new Promise((resolve, reject) => {
			this._vm.$api.delete(`campaigns/${id}`)
				.then(() => {
					commit('removeSplitByIndex', { campaignIndex, splitIndex })
					resolve()
				})
				.catch(error => {
					addError(commit, error, 'Campaign split test could not be deleted.')
					reject(error)
					console.error({ error, campaignId: id })
				})
		})
	},
	fillCampaignList ({ state, getters, dispatch }, limit) {
		if (getters.moreAvailable) {
			dispatch('fetchDashboard', {
				force   : true,
				loading : 'campaigns-refresh',
				offset  : state.campaigns.length,
				limit,
			})
		}
	},
	bulkArchiveCampaigns ({ dispatch }, { campaigns }) {
		return dispatch('bulkTrashCampaigns', {
			campaigns,
			toArchive : true,
		})
	},
	bulkTrashCampaigns ({ dispatch, commit }, { campaigns, toArchive = false }) {
		return new Promise((resolve, reject) => {
			this._vm.$api
				.post(`campaigns/batch/${toArchive ? 'archive' : 'trash'}`)
				.send({
					campaigns : campaigns,
				})
				.then(() => {
					dispatch('deSelectAll')
					dispatch('refreshDashboard')
					resolve()
				})
				.catch(error => {
					addError(commit, error, 'Something went wrong while trashing campaigns.')
					reject(error)
					console.error(error)
				})
		})
	},
	makePrimaryCampaign ({ commit }, campaign) {
		return new Promise((resolve, reject) => {
			this._vm.$api
				.post(`campaigns/${campaign.id}/primary`)
				.then(() => {
					// first get an array of all other splits.
					commit('makePrimary', campaign)
					commit('updateSort')
					resolve()
				})
				.catch(error => {
					addError(commit, error, 'Something went wrong while setting the primary campaign.')
					reject(error)
				})
		})
	},
	saveWordPress ({ dispatch, commit }, payload) {
		return new Promise((resolve, reject) => {
			return this._vm.$api
				.post({ wpEndpoint: `omapp/v1/campaigns/${payload.campaignId}` })
				.send(payload.settings)
				.then(() => {
					resolve()
				})
				.catch(error => {
					addError(commit, error, 'Something went wrong while saving WordPress settings.')
					reject(error)
					console.error({ error })
				})
		})
	},
	selectAll ({ rootState, state, commit, rootGetters }) {
		commit('selectAll', true)
		commit('setBulk', state.campaigns
			.filter(c => rootGetters.allowed('roles', 'campaign.edit', c))
			.map(c => c.id))
	},
	deSelectAll ({ commit }) {
		commit('selectAll', false)
		commit('clearBulk')
	},
	fetchFolders ({ commit, dispatch, rootGetters }) {
		return new Promise((resolve, reject) => {
			if (rootGetters.isFetched('folders') || rootGetters.isFetching('folders')) {
				return resolve()
			}

			commit('fetching', 'folders', { root: true })

			this._vm.$api
				.get('folders')
				.then(({ body }) => {
					dispatch('setFolders', body)
					resolve()
				})
				.catch(error => {
					reject(error)
					console.error({ error })
				})
		})
	},
	setFolders ({ commit }, payload) {
		commit('fetched', 'folders', { root: true })
		commit('setFolders', payload)
	},
	fetchRulesetData ({ commit, rootGetters }) {
		return new Promise((resolve, reject) => {
			if (rootGetters.isFetched('rulesetData') || rootGetters.isFetching('rulesetData')) {
				return resolve(appData.get('rulesetData'))
			}

			// Check if we have cached data.
			const cached = storage.local.getItem('omRulesetData')

			const handleFail = error => {
				if (!cached) {
					// Only reject if we don't already have some cached data.
					reject(error)
				}

				// Log the error either way.
				console.error({ error })
			}

			const handleData = data => {
				appData.set('rulesetData', ruleHelpers.setup(data))
				commit('fetched', 'rulesetData', { root: true })
				resolve(appData.get('rulesetData'))
			}

			if (cached) {
				// If we have cached data, setup our data.
				handleData(cached)
			} else {
				// No cached data, so set the fetching param.
				commit('fetching', 'rulesetData', { root: true })
			}

			// Either way, we fetch fresh. This ensures the cache is continually updated.
			const url = this._vm.$urls.api(`ruleset-data?timestamp=${dates.timestamp()}`)
			requests
				.get(url)
				.set({
					'X-OptinMonster-SKey' : '$2y$10$0V74hDEXsBMF4d4rrPnEAuXWTabR1IIs7bTDwY1Ubuhctzhwf47ZC',
				})
				.then(({ body }) => {
					handleData(body)
					storage.local.setItem('omRulesetData', body)
				})
				.catch(handleFail)
		})
	},
	setPageVars ({ commit }, { search = null, page = null }) {
		if (null !== search) {
			commit('setSearch', search)
		}

		if (null !== page) {
			commit('page', page)
		}
	},
	goToPage ({ state, dispatch }, { search = null, page = null }) {
		const params = {}
		let name = 'campaigns-paginated'

		dispatch('setPageVars', { search, page })

		if (state.search) {
			params.searchTerm = state.search
			name = 'campaigns-search'
		}

		if (state.page) {
			params.campaignsPage = state.page
			if (params.searchTerm) {
				name = 'campaigns-paginated-search'
			}
		}

		router.push({ name, params })
	},
}
